import React, { useContext, useEffect, useState } from 'react';
import { AccountContext } from '../../AccountContext';
import { formatCustomerName, toFormattedDate } from '@app/utilities';
import { Link, useSearchParams } from 'react-router-dom';
import { Arudd, LastPaymentDetails } from './Arudd';
import { Auddis } from './Auddis';
import { Addacs } from './Addacs';
import { JsonViewer } from '@textea/json-viewer';
import {
  useAccessPayConfiguration,
  useCheckReports,
  useClearReports,
  useGetPaymentHistory,
  useGetReports,
  useRegularPayments
} from '@app/sdk';
import { useCustomerByAccountNumber } from '../../../../sdk/cus';
import { useServiceUserNumbers } from '../../../../sdk/tms/tenantHierarchy/useServiceUserNumbers';
import { useGlobalContext } from '../../../../GlobalContextProvider';

function getIsoDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function Reports(props: { accountNumber: string }) {
  const [searchParam, setSearchParam] = useSearchParams();
  const lastReportType = searchParam.get('reportType');

  const { accountId, billingDate } = useContext(AccountContext);
  const { tenantId } = useGlobalContext();
  const [accessPayBaseUrl, setAccessPayBaseUrl] = useState<string>();
  const [reportType, setReportType] = useState<string>(lastReportType ?? 'arudd');
  const [selectedServiceUser, SetServiceUser] = useState<string>("");
  const [regularPayment, setRegularPayment] = useState(undefined as any);
  const [checkedReportJson, setCheckedReportJson] = useState(undefined as any);
  const [lastPaymentDate, setLastPaymentDate] = useState<LastPaymentDetails>({
    date: getIsoDate(billingDate),
    amount: 0,
  });

  const { mutateAsync: checkReports, isPending: isCheckingReports } = useCheckReports();
  const { mutateAsync: clearReports } = useClearReports();

  const { data: reports, isLoading: isGettingReports } = useGetReports({ reportType, accessPayBaseUrl, serviceUserNumber: selectedServiceUser });
  const { data: accessPayConfig } = useAccessPayConfiguration();

  async function handleCheckReports() {
    const result = await checkReports({ reportType });
    setCheckedReportJson(result);
  }

  async function handleClearReports() {
    setCheckedReportJson(null);
    await clearReports();
  }

  function updateReportType(reportType: string) {
    setReportType(reportType);
    setSearchParam({ reportType: reportType });
  }

  const { data: regularPayments, isLoading: isGettingRegularPayment } = useRegularPayments({ accountId });
  const { data: paymentHistory, isLoading: isGettingCustomer } = useGetPaymentHistory({ accountId });
  const { data: customer } = useCustomerByAccountNumber(props.accountNumber);
  const { data: serviceUsers, isLoading: isServiceUserLoading } = useServiceUserNumbers(tenantId);
  
  useEffect(() => {
    if (!paymentHistory) {
      setLastPaymentDate({ date: getIsoDate(billingDate), amount: 0 });
      return;
    }

    const history = paymentHistory.history;
    if (history.length > 0) {
      const lastPayment = history[history.length - 1];
      const collectionDate = toFormattedDate(lastPayment.collectionDate);
      setLastPaymentDate({ date: collectionDate, amount: lastPayment.amount?.value ?? 0 });
    }
  }, [billingDate, paymentHistory]);

  useEffect(() => {
    const regularPayment = regularPayments?.planned ?? regularPayments?.current;
    setRegularPayment(regularPayment);
  }, [regularPayments]);

  useEffect(() => {
    if (serviceUsers && serviceUsers.length > 0) {
      SetServiceUser(serviceUsers[0].properties.Id); // Set the first element as selected
    }
  }, [serviceUsers]);

  useEffect(() => {
    setAccessPayBaseUrl(accessPayConfig?.baseUrl);
  }, [accessPayConfig]);
  
  return (
    <>
      <h2>Reports</h2>
      {isGettingReports && <div className="ui active centered inline loader" />}

      {!isGettingReports && (
        <div className="ui form" style={{ marginBottom: '15px' }}>
          <div className="field">
            <label>Report Type</label>
            <select onChange={(e) => updateReportType(e.target.value)} value={reportType} style={{ marginLeft: '5px' }}>
              <option>--Select a report type--</option>
              <option value="addacs">Automated Direct Debit Amendment and Cancellation Service (ADDACS)</option>
              <option value="arudd">Automated Return of Unpaid Direct Debits Service (ARUDD)</option>
              <option value="auddisinput">Automated Direct Debit Instruction Service (AUDDIS)</option>
            </select>
          </div>
          <div className="field">
            <label>Service User Number</label>
            { !isServiceUserLoading ? 
             <select onChange={(e) => SetServiceUser(e.target.value)} value={selectedServiceUser}>
              {serviceUsers.map(s => (
                <option key={s.id} value={s.properties.Id}>{s.properties.Id}</option>
              ))}
             </select>
              : null}
          </div>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
        }}>
        <div>
          {!isGettingReports && (!reports || reports.length == 0) && <div>No reports found</div>}

          {!isGettingReports && reports && reports.length > 0 && (
            <table className="ui compact table" style={{ marginBottom: '20px' }}>
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Received Time</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((s: any) => (
                  <tr key={s.id}>
                    <td>
                      <Link to={`/accounts/${accountId}/reports/${s.id}?reportType=${s.type}`}>{s.id}</Link>
                    </td>
                    <td>{toFormattedDate(s.receivedTime)}</td>
                    <td>{s.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          {!isGettingRegularPayment && !isGettingCustomer && regularPayment && customer && (
            <div>
              <h4>New for Account</h4>
              {reportType == 'arudd' && (
                <Arudd
                  accountNumber={props.accountNumber}
                  bankAccountNumber={regularPayment.bankAccountNumber}
                  customerName={formatCustomerName(customer)}
                  sortCode={regularPayment.sortCode}
                  lastPaymentDetails={lastPaymentDate}
                  serviceUserNumber={selectedServiceUser}
                />
              )}
              {reportType == 'auddisinput' && (
                <Auddis
                  accountNumber={props.accountNumber}
                  bankAccountNumber={regularPayment.bankAccountNumber}
                  customerName={formatCustomerName(customer)}
                  sortCode={regularPayment.sortCode}
                  serviceUserNumber={selectedServiceUser}
                />
              )}
              {reportType == 'addacs' && (
                <Addacs
                  accountNumber={props.accountNumber}
                  bankAccountNumber={regularPayment.bankAccountNumber}
                  customerName={formatCustomerName(customer)}
                  sortCode={regularPayment.sortCode}
                  serviceUserNumber={selectedServiceUser}
                />
              )}
            </div>
          )}
        </div>
        <div>
          <div
            className="ui"
            style={{
              display: 'flex',
              justifyContent: 'space-evenly',
              marginBottom: '10px',
            }}>
            <div>
              <button
                className={'ui button'}
                type="button"
                onClick={async () => await handleCheckReports()}
                disabled={!reports || reports.length <= 0}>
                <i className="ui icon play" />
                Check Reports
              </button>
            </div>
            <div>
              <button
                className={'ui button'}
                type="button"
                onClick={async () => await handleClearReports()}
                disabled={!reports || reports.length <= 0}>
                <i className="ui icon play" />
                Clear Reports
              </button>
            </div>
          </div>
          {!isCheckingReports && checkedReportJson && <JsonViewer value={checkedReportJson} />}
        </div>
      </div>
    </>
  );
}
